import * as Sentry from '@sentry/browser';
import { SEARCH, NURSURY, PLANTS, NURSERY_SEARCH, REQUEST_PLANT, NEWSLETTER_SUBSCRIBE, FEATURE, INDEX_DATA, COMMON_DATA, NAVIGATION_DATA, ADD_ITEMS, CART_CONTENTS, CLEAR_CART, CSRF_TOKEN, AUTO_SEARCH, } from '../actions/constats';
import { getCookie } from '../utils/helpers';
// Endpoints in ../actions/constats all start with '/', so strip any trailing
// slash from the base to avoid emitting a scheme-relative '//host/path' URL.
const BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/+$/, '');
const defaultHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
};
async function handleResponse(response) {
    if (response.ok) {
        return response.json();
    }
    const data = await response.json().catch(() => null);
    const error = {
        status: response.status,
        statusText: response.statusText,
        data,
    };
    return Promise.reject(error);
}
// Rejections that came out of a request, and so have already been dealt with here.
const handled = new WeakSet();
function markHandled(error) {
    if (typeof error === 'object' && error !== null) {
        handled.add(error);
    }
}
// Requests the caller aborted, and requests that never left the machine, are noise.
// fetch() signals the latter by rejecting with a TypeError, and that is the only way
// it can produce one for a request we built ourselves, so test the type rather than
// the message: the message is not specified and differs per browser ('Failed to
// fetch' in Chrome, 'NetworkError when attempting to fetch resource.' in Firefox,
// 'Load failed' in Safari).
function isExpectedFailure(error) {
    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    const isNetworkFailure = error instanceof TypeError;
    return isAbort || isNetworkFailure;
}
function reportUnexpected(error, report = true) {
    markHandled(error);
    if (report && !isExpectedFailure(error)) {
        Sentry.captureException(error);
    }
}
// For callers with nothing to do about a failed request. A bare `.catch(() => {})`
// would also hide bugs thrown from their own `.then()`, so report anything that
// did not come from the request itself.
export function ignoreRequestFailure(error) {
    if (typeof error === 'object' && error !== null && handled.has(error)) {
        return;
    }
    Sentry.captureException(error);
}
function get(url, options, reportErrors = true) {
    return fetch(`${BASE_URL}${url}`, {
        ...options,
        headers: { ...defaultHeaders, ...options === null || options === void 0 ? void 0 : options.headers },
    })
        .then(handleResponse)
        .catch((error) => {
        reportUnexpected(error, reportErrors);
        return Promise.reject(error);
    });
}
function post(url, data, options) {
    const csrfToken = getCookie('csrftoken');
    // Callers only ever pass plain object headers, so treat them as such
    const headers = {
        ...defaultHeaders,
        ...options === null || options === void 0 ? void 0 : options.headers,
    };
    if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
    }
    if (data instanceof FormData) {
        // Let the browser set Content-Type so that it includes the multipart boundary
        delete headers['Content-Type'];
    }
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return fetch(`${BASE_URL}${url}`, {
        ...options,
        method: 'POST',
        headers,
        body,
    })
        .then(handleResponse)
        .catch((error) => {
        reportUnexpected(error);
        return Promise.reject(error);
    });
}
const verifyCsrfExists = async () => {
    // If we are going to do a POST but don't have a csrf token, we need to get one.
    // If we access the PDP as the first page (rendered by SSR), we won't pass through django
    // and therefore one won't be set. This endpoint exists only to set the cookie.
    const cookie = getCookie('csrftoken');
    if (cookie === '') {
        await get(CSRF_TOKEN);
    }
};
export default {
    home: {
        getIndexData: () => get(INDEX_DATA),
        getCommonData: () => get(COMMON_DATA),
        getNavigationData: () => get(NAVIGATION_DATA),
        generalSearch: (slug) => get(SEARCH + slug),
        getAny: (url) => get(url),
    },
    nursery: {
        updateSearchResult: (query) => get(NURSERY_SEARCH + query),
        nurserySearch: (slug) => get(NURSURY + slug),
    },
    featured: {
        getFeatured: (featureId) => get(FEATURE + featureId),
    },
    plant: {
        getPlants: (plantName) => get(PLANTS + plantName),
        requestPlants: async (data) => {
            await verifyCsrfExists();
            return post(REQUEST_PLANT, data);
        },
    },
    search: {
        // Fires on every keystroke, so a degraded search backend would otherwise send
        // Sentry one event per character typed. Failures here are already visible
        // server-side, and the caller has nothing to do about them but drop the result.
        autocomplete: (query, options) => get(`${AUTO_SEARCH}${query}&${new Date().getTime()}`, options, false),
    },
    checkout: {
        addItems: async (data) => {
            await verifyCsrfExists();
            return post(ADD_ITEMS, data);
        },
        getCartList: async (params = '') => get(CART_CONTENTS + params),
        clearCart: async () => {
            await verifyCsrfExists();
            return post(CLEAR_CART);
        },
    },
    newsLetter: {
        subscribe: async (data) => {
            await verifyCsrfExists();
            return post(NEWSLETTER_SUBSCRIBE, data);
        },
    },
};
